/**
 * Unhandled Promise Rejection Tracker
 *
 * This utility sets up a global listener for unhandled promise rejections
 * to help identify and fix async code that doesn't properly handle errors.
 *
 * Usage:
 * - Import and call setupUnhandledRejectionTracker() early in app initialization
 * - Active in all environments
 * - Dev/staging: Logs detailed info to console + Sentry breadcrumbs + Sentry capture
 * - Production: Sentry breadcrumbs + Sentry capture (no console noise)
 */

import { addBreadcrumb, withScope, captureException, captureMessage } from '@sentry/react';
import { config } from '@/config/config';
import { isTimeoutError } from '@/shared/utils/asyncHelpers';
import { getBootId, getTimeSinceBoot } from '@/core/utils/bootSession';

// Track if we've already set up the listener to prevent duplicates
let isSetup = false;

// Track recent rejections to help identify patterns
const recentRejections: Array<{
  timestamp: number;
  route: string;
  errorType: string;
  message: string;
}> = [];
const MAX_RECENT_REJECTIONS = 20;

/**
 * Extract useful information from an unhandled rejection error
 */
function extractErrorInfo(reason: unknown): {
  errorType: string;
  message: string;
  status?: string | number;
  isTimeout: boolean;
  stack?: string;
} {
  const isTimeout = isTimeoutError(reason);

  if (!reason) {
    return {
      errorType: 'unknown',
      message: 'Rejection with no reason',
      isTimeout: false,
    };
  }

  if (reason instanceof Error) {
    return {
      errorType: reason.name || 'Error',
      message: reason.message,
      stack: reason.stack,
      isTimeout,
    };
  }

  if (typeof reason === 'object') {
    const obj = reason as Record<string, unknown>;
    return {
      errorType: String(obj.status || obj.name || 'Object'),
      message: String(obj.message || obj.error || JSON.stringify(reason).slice(0, 200)),
      status: obj.status as string | number | undefined,
      isTimeout,
      stack: typeof obj.stack === 'string' ? obj.stack : undefined,
    };
  }

  return {
    errorType: typeof reason,
    message: String(reason),
    isTimeout,
  };
}

/**
 * Get current route information for context
 */
function getCurrentRoute(): string {
  if (typeof window === 'undefined') return 'server';
  return window.location.pathname + window.location.search;
}

/**
 * Get boot context from boot session utility
 */
function getBootContext(): Record<string, unknown> {
  try {
    return {
      bootId: getBootId(),
      timeSinceBoot: getTimeSinceBoot(),
    };
  } catch {
    return {};
  }
}

/**
 * Handle an unhandled promise rejection
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const errorInfo = extractErrorInfo(event.reason);
  const route = getCurrentRoute();
  const bootContext = getBootContext();

  // Track the rejection
  recentRejections.push({
    timestamp: Date.now(),
    route,
    errorType: errorInfo.errorType,
    message: errorInfo.message,
  });
  if (recentRejections.length > MAX_RECENT_REJECTIONS) {
    recentRejections.shift();
  }

  // Build log context
  const logContext = {
    errorType: errorInfo.errorType,
    message: errorInfo.message,
    status: errorInfo.status,
    isTimeout: errorInfo.isTimeout,
    route,
    ...bootContext,
    stack: errorInfo.stack,
    recentRejectionCount: recentRejections.filter(
      (r) => Date.now() - r.timestamp < 60000
    ).length,
  };

  // Console logging with distinctive styling (dev/staging only to avoid noise in production)
  if (config.env !== 'production') {
    const logLevel = errorInfo.isTimeout ? 'warn' : 'error';
    const label = errorInfo.isTimeout ? 'TIMEOUT' : 'UNHANDLED REJECTION';

    console[logLevel](
      `%c${label}`,
      'background: #dc2626; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
      '\n',
      logContext
    );

    if (errorInfo.stack) {
      console[logLevel]('Stack trace:', errorInfo.stack);
    }
  }

  // Add Sentry breadcrumb for tracking
  addBreadcrumb({
    category: 'unhandled-rejection',
    message: `${errorInfo.errorType}: ${errorInfo.message}`,
    level: errorInfo.isTimeout ? 'warning' : 'error',
    data: {
      route,
      errorType: errorInfo.errorType,
      status: errorInfo.status,
      isTimeout: errorInfo.isTimeout,
      ...bootContext,
    },
  });

  // Capture to Sentry in staging and production for monitoring
  // Use captureMessage for visibility - these are often silent failures
  if (config.env === 'staging' || config.env === 'production') {
    withScope((scope) => {
      scope.setTag('error_type', errorInfo.errorType);
      scope.setTag('is_timeout', String(errorInfo.isTimeout));
      scope.setTag('route', route);
      scope.setFingerprint(['unhandled-rejection', errorInfo.errorType, route]);
      scope.setExtra('context', logContext);

      // Capture as exception if we have a proper error, otherwise as message
      if (errorInfo.stack) {
        const error = new Error(`Unhandled rejection: ${errorInfo.message}`);
        error.name = errorInfo.errorType;
        error.stack = errorInfo.stack;
        captureException(error);
      } else {
        captureMessage(`Unhandled rejection: ${errorInfo.errorType}`, {
          level: errorInfo.isTimeout ? 'warning' : 'error',
        });
      }
    });
  }
}

/**
 * Set up the global unhandled rejection tracker.
 * Call this early in app initialization (e.g., in _app.tsx or a provider).
 *
 * Active in all environments:
 * - Production: Captures to Sentry silently (no console output)
 * - Staging/Dev: Console logging + Sentry capture
 */
export function setupUnhandledRejectionTracker(): void {
  // Only run in browser
  if (typeof window === 'undefined') return;

  // Only set up once
  if (isSetup) return;

  // Active in all environments - production gets Sentry capture,
  // dev/staging get console logging too

  isSetup = true;

  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Log that tracking is active (dev/staging only)
  if (config.env !== 'production') {
    console.info(
      '%c[UnhandledRejectionTracker] Active',
      'background: #2563eb; color: white; padding: 2px 6px; border-radius: 3px;',
      `Environment: ${config.env}`
    );
  }
}

/**
 * Tear down the global unhandled rejection tracker.
 * Removes the event listener and resets the setup flag so it can be re-initialized.
 * Useful for testing or cleanup.
 */
export function teardownUnhandledRejectionTracker(): void {
  if (typeof window === 'undefined') return;
  if (!isSetup) return;

  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  isSetup = false;
}

/**
 * Get recent unhandled rejections for debugging.
 * Useful for displaying in dev tools or health panels.
 */
export function getRecentRejections(): typeof recentRejections {
  return [...recentRejections];
}

/**
 * Clear recent rejections history.
 * Useful for testing or resetting state.
 */
export function clearRecentRejections(): void {
  recentRejections.length = 0;
}
